import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Radio,
  Segmented,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createDailyMessage,
  deleteDailyMessage,
  listDailyMessages,
  updateDailyMessage,
} from '../../api/dailyMessages';
import './index.css';

const statusOptions = [
  { label: '未展示', value: 'PENDING' },
  { label: '已展示', value: 'DISPLAYED' },
];

const placementOptions = [
  { label: '插入未展示最前面', value: 'FRONT' },
  { label: '追加到未展示最后面', value: 'BACK' },
];

const validateContent = (_, value) => {
  const content = String(value || '').trim();
  if (!content) return Promise.reject(new Error('请输入寄语内容'));
  if ([...content].length > 60) return Promise.reject(new Error('单条寄语最多 60 个字符'));
  return Promise.resolve();
};

const validateAuthor = (_, value) => {
  const author = String(value || '').trim();
  if (!author) return Promise.reject(new Error('请输入作者或出处'));
  if ([...author].length > 40) return Promise.reject(new Error('作者或出处最多 40 个字符'));
  return Promise.resolve();
};

const DailyMessages = () => {
  const [entryForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('query');
  const [status, setStatus] = useState('PENDING');
  const [keyword, setKeyword] = useState('');
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadRecords = useCallback(async ({
    nextStatus = status,
    nextPage = pagination.current,
    nextSize = pagination.pageSize,
    nextKeyword = keyword,
  } = {}) => {
    setLoading(true);
    setLoadError('');
    try {
      const result = await listDailyMessages({
        status: nextStatus,
        keyword: nextKeyword.trim() || undefined,
        page: nextPage,
        size: nextSize,
      });
      setRecords(result.list || []);
      setPagination({
        current: result.page || nextPage,
        pageSize: result.size || nextSize,
        total: result.total || 0,
      });
    } catch (error) {
      setLoadError(error?.message || '寄语加载失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, pagination, status]);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPagination((current) => ({ ...current, current: 1 }));
    loadRecords({ nextStatus, nextPage: 1 });
  };

  const search = () => {
    setPagination((current) => ({ ...current, current: 1 }));
    loadRecords({ nextPage: 1 });
  };

  const changePage = (page, pageSize) => {
    setPagination((current) => ({ ...current, current: page, pageSize }));
    loadRecords({ nextPage: page, nextSize: pageSize });
  };

  const submitEntry = async () => {
    const values = await entryForm.validateFields();
    setSaving(true);
    try {
          await createDailyMessage({
            content: values.content.trim(),
            author: values.author.trim(),
            placement: values.placement,
      });
      message.success('寄语已录入');
      entryForm.resetFields();
      entryForm.setFieldsValue({ placement: 'BACK' });
      setStatus('PENDING');
      setActiveTab('query');
      await loadRecords({ nextStatus: 'PENDING', nextPage: 1, nextKeyword: '' });
      setKeyword('');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    editForm.setFieldsValue({ content: record.content, author: record.author });
  };

  const submitEdit = async () => {
    const values = await editForm.validateFields();
    setSaving(true);
    try {
      await updateDailyMessage(editing.id, {
        content: values.content.trim(),
        author: values.author.trim(),
      });
      message.success('寄语已更新');
      setEditing(null);
      await loadRecords();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (record) => {
    await deleteDailyMessage(record.id);
    message.success('寄语已删除');
    const nextPage = records.length === 1 && pagination.current > 1
      ? pagination.current - 1
      : pagination.current;
    await loadRecords({ nextPage });
  };

  const columns = [
    {
      title: '寄语内容',
      dataIndex: 'content',
      render: (value) => <Typography.Text>{value}</Typography.Text>,
    },
    {
      title: '作者/出处',
      dataIndex: 'author',
      width: 160,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => (
        <Tag color={value === 'DISPLAYED' ? 'success' : 'processing'}>
          {value === 'DISPLAYED' ? '已展示' : '未展示'}
        </Tag>
      ),
    },
    {
      title: '首次展示日期',
      dataIndex: 'firstDisplayedDate',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      width: 180,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm
            title="删除寄语"
            description={record.status === 'DISPLAYED'
              ? '删除后管理列表将不再显示，历史发布内容不会改变。'
              : '删除后该寄语不会再参与每日展示。'}
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => remove(record)}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const queryContent = (
    <div className="daily-message-query">
      <div className="daily-message-toolbar">
        <Segmented options={statusOptions} value={status} onChange={changeStatus} />
        <Space.Compact className="daily-message-search">
          <Input
            allowClear
            value={keyword}
            placeholder="搜索名言内容或作者"
            onChange={(event) => setKeyword(event.target.value)}
            onPressEnter={search}
          />
          <Button icon={<SearchOutlined />} onClick={search}>查询</Button>
        </Space.Compact>
        <Button icon={<ReloadOutlined />} onClick={() => loadRecords()}>刷新</Button>
      </div>
      {loadError ? (
        <Alert
          showIcon
          type="error"
          message={loadError}
          action={<Button size="small" onClick={() => loadRecords()}>重试</Button>}
        />
      ) : null}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={records}
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description={status === 'PENDING' ? '暂无未展示寄语' : '暂无已展示寄语'} /> }}
        scroll={{ x: 900 }}
      />
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        showSizeChanger
        showTotal={(total) => `共 ${total} 条`}
        onChange={changePage}
      />
    </div>
  );

  const entryContent = (
    <div className="daily-message-entry">
      <Form form={entryForm} layout="vertical" initialValues={{ placement: 'BACK' }}>
        <Form.Item name="content" label="寄语内容" rules={[{ validator: validateContent }]}>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 8 }}
            placeholder="输入一条首页寄语"
            showCount
            maxLength={60}
          />
        </Form.Item>
        <Form.Item name="author" label="作者/出处" rules={[{ validator: validateAuthor }]}>
          <Input placeholder="例如：彼得·德鲁克" maxLength={40} showCount />
        </Form.Item>
        <Form.Item name="placement" label="加入未展示队列的位置" rules={[{ required: true }]}>
          <Radio.Group options={placementOptions} optionType="button" buttonStyle="solid" />
        </Form.Item>
        <Alert
          showIcon
          type="info"
          message="每天按未展示队列顺序取一条；插入最前面将在下一个尚未发布的日期优先展示。"
        />
        <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={submitEntry}>
          录入寄语
        </Button>
      </Form>
    </div>
  );

  return (
    <div className="daily-message-page">
      <header className="daily-message-header">
        <div>
          <div className="daily-message-title">
            <MessageOutlined />
            <Typography.Title level={2}>今日寄语</Typography.Title>
          </div>
          <Typography.Text type="secondary">管理 Android 首页按日顺序展示的寄语队列</Typography.Text>
        </div>
      </header>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'query', label: '查询', children: queryContent },
          { key: 'entry', label: '录入', children: entryContent },
        ]}
      />
      <Modal
        title="编辑寄语"
        open={Boolean(editing)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        onOk={submitEdit}
        onCancel={() => setEditing(null)}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="content" label="寄语内容" rules={[{ validator: validateContent }]}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} showCount maxLength={60} />
          </Form.Item>
          <Form.Item name="author" label="作者/出处" rules={[{ validator: validateAuthor }]}>
            <Input maxLength={40} showCount />
          </Form.Item>
          {editing?.status === 'DISPLAYED' ? (
            <Alert showIcon type="warning" message="编辑不会修改已经发布的历史内容。" />
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default DailyMessages;
